import React, { useEffect, useState } from 'react';
import './PwaInstallPrompt.css';
import { useLanguage } from '../i18n/LanguageContext';

const DISMISS_KEY = 'moviq-pwa-install-dismissed';

const isStandalone = () => {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  return String(document.referrer || '').startsWith('android-app://');
};

const isIosDevice = () => {
  const ua = window.navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  return iOS && !window.MSStream;
};

const isIpadDevice = () => {
  const ua = window.navigator.userAgent || '';
  return /iPad/.test(ua)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
};

const isIosSafari = () => {
  const ua = window.navigator.userAgent || '';
  if (!isIosDevice()) return false;
  const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|Brave/i.test(ua);
  return !isOtherBrowser;
};

const isAndroidDevice = () => /Android/i.test(window.navigator.userAgent || '');

const ShareIcon = () => (
  <svg className="pwa-ios-step-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3v11M8.5 6.5 12 3l3.5 3.5M7 13v5.5A1.5 1.5 0 0 0 8.5 20h7a1.5 1.5 0 0 0 1.5-1.5V13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlusSquareIcon = () => (
  <svg className="pwa-ios-step-icon" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 8v8M8 12h8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PwaInstallPrompt = () => {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showNative, setShowNative] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') {
      return undefined;
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowNative(true);
      setShowManual(false);
    };

    const onInstalled = () => {
      localStorage.setItem(DISMISS_KEY, '1');
      setDeferredPrompt(null);
      setShowNative(false);
      setShowManual(false);
      setShowIosGuide(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    const timer = window.setTimeout(() => {
      if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') return;
      if (isIosDevice() || isAndroidDevice()) setShowManual(true);
    }, 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      window.clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShowNative(false);
    setShowManual(false);
    setShowIosGuide(false);
    setDeferredPrompt(null);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch (_err) {
        // Native sheet closed without installing.
      }
      setDeferredPrompt(null);
      setShowNative(false);
      return;
    }
    if (isIosDevice()) {
      setShowIosGuide(true);
    }
  };

  const nativeReady = showNative && deferredPrompt;
  const ios = isIosDevice();
  const manualReady = showManual && !nativeReady && !isStandalone();
  const showBanner = (nativeReady || manualReady) && !showIosGuide;

  if (!showBanner && !showIosGuide) return null;

  const canInstall = nativeReady || ios;
  const title = t('Install Moviq');
  const body = nativeReady || ios
    ? t('Add Moviq to your home screen for quicker booking.')
    : t('Open the browser menu and tap Install app.');

  return (
    <>
      {showBanner ? (
        <div className="pwa-install-banner" role="dialog" aria-labelledby="pwa-install-title">
          <img
            className="pwa-install-icon"
            src={`${process.env.PUBLIC_URL || ''}/images/pwa-icon-192.png`}
            alt=""
          />
          <div className="pwa-install-copy">
            <p id="pwa-install-title" className="pwa-install-title">{title}</p>
            <p className="pwa-install-body">{body}</p>
          </div>
          <div className="pwa-install-actions">
            {canInstall ? (
              <button type="button" className="pwa-install-primary" onClick={install}>
                {t('Install')}
              </button>
            ) : null}
            <button type="button" className="pwa-install-secondary" onClick={dismiss}>
              {t('Not now')}
            </button>
          </div>
        </div>
      ) : null}

      {showIosGuide ? (
        <div className="pwa-ios-guide" role="dialog" aria-labelledby="pwa-ios-guide-title">
          <button type="button" className="pwa-ios-guide-close" onClick={dismiss} aria-label={t('Close')}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <img
            className="pwa-ios-guide-app-icon"
            src={`${process.env.PUBLIC_URL || ''}/images/pwa-icon-192.png`}
            alt=""
          />
          <h2 id="pwa-ios-guide-title" className="pwa-ios-guide-title">{title}</h2>
          <p className="pwa-ios-guide-lead">
            {isIosSafari()
              ? t('Follow these steps in Safari:')
              : t('Open this page in Safari, then follow these steps:')}
          </p>
          <ol className="pwa-ios-guide-steps">
            <li>
              <ShareIcon />
              <span>{t('Tap the Share button in Safari.')}</span>
            </li>
            <li>
              <PlusSquareIcon />
              <span>{t('Tap Add to Home Screen.')}</span>
            </li>
            <li>
              <span className="pwa-ios-step-number">3</span>
              <span>{t('Tap Add.')}</span>
            </li>
          </ol>
          {!isIpadDevice() ? <div className="pwa-ios-guide-arrow" aria-hidden="true" /> : null}
        </div>
      ) : null}
    </>
  );
};

export default PwaInstallPrompt;
